import type { PermissionApi } from "../../api/PermissionApi";
import type { CreateRole } from "../../generated/dto/CreateRole";
import type { CreateRoleAssignment } from "../../generated/dto/CreateRoleAssignment";
import type { CreateRolePermission } from "../../generated/dto/CreateRolePermission";
import type { CreateScopes } from "../../generated/dto/CreateScopes";
import type { CreateUserPermission } from "../../generated/dto/CreateUserPermission";
import type { DeleteRoleAssignmentStaff } from "../../generated/dto/DeleteRoleAssignmentStaff";
import type { DeleteRolePermissionStaff } from "../../generated/dto/DeleteRolePermissionStaff";
import type { DeleteRoleStaff } from "../../generated/dto/DeleteRoleStaff";
import type { DeleteScopesStaff } from "../../generated/dto/DeleteScopesStaff";
import type { DeleteUserPermissionStaff } from "../../generated/dto/DeleteUserPermissionStaff";
import type { RoleAssignmentFetchOptions } from "../../generated/dto/RoleAssignmentFetchOptions";
import type { RoleAssignmentStaff } from "../../generated/dto/RoleAssignmentStaff";
import type { RoleFetchOptions } from "../../generated/dto/RoleFetchOptions";
import type { RolePermissionFetchOptions } from "../../generated/dto/RolePermissionFetchOptions";
import type { RolePermissionStaff } from "../../generated/dto/RolePermissionStaff";
import type { RoleStaff } from "../../generated/dto/RoleStaff";
import type { ScopesFetchOptions } from "../../generated/dto/ScopesFetchOptions";
import type { ScopesStaff } from "../../generated/dto/ScopesStaff";
import type { UpdateRole } from "../../generated/dto/UpdateRole";
import type { UpdateRoleAssignment } from "../../generated/dto/UpdateRoleAssignment";
import type { UpdateRolePermission } from "../../generated/dto/UpdateRolePermission";
import type { UpdateScopes } from "../../generated/dto/UpdateScopes";
import type { UpdateUserPermission } from "../../generated/dto/UpdateUserPermission";
import type { UserPermissionFetchOptions } from "../../generated/dto/UserPermissionFetchOptions";
import type { UserPermissionStaff } from "../../generated/dto/UserPermissionStaff";
import type { UserStaff } from "../../generated/dto/UserStaff";
import type { UserStaffFetchOptions } from "../../generated/dto/UserStaffFetchOptions";

export class PermissionFrontendService {
  constructor(private readonly api: PermissionApi) {}
  private readonly scopesStaffCache: Map<string, ScopesStaff> = new Map();
  private readonly roleStaffCache: Map<number, RoleStaff> = new Map();
  async getScopesStaff(id: string, options?: ScopesFetchOptions): Promise<ScopesStaff> {
    const __cached = this.scopesStaffCache.get(id);
    if (__cached !== undefined) {
      return __cached
    }
    const __value = await this.api.getScopesStaff(id, options);
    this.scopesStaffCache.set(id, __value);
    return __value
  }
  async getRoleStaff(id: number, options?: RoleFetchOptions): Promise<RoleStaff> {
    const __cached = this.roleStaffCache.get(id);
    if (__cached !== undefined) {
      return __cached
    }
    const __value = await this.api.getRoleStaff(id, options);
    this.roleStaffCache.set(id, __value);
    return __value
  }
  async getRolePermissionStaff(id: number, options?: RolePermissionFetchOptions): Promise<RolePermissionStaff> {
    return this.api.getRolePermissionStaff(id, options)
  }
  async getRoleAssignmentStaff(id: number, options?: RoleAssignmentFetchOptions): Promise<RoleAssignmentStaff> {
    return this.api.getRoleAssignmentStaff(id, options)
  }
  async getUserPermissionStaff(id: number, options?: UserPermissionFetchOptions): Promise<UserPermissionStaff> {
    return this.api.getUserPermissionStaff(id, options)
  }
  async getAllScopesStaffs(options?: ScopesFetchOptions): Promise<Array<ScopesStaff>> {
    return this.api.getAllScopesStaffs(options)
  }
  async getAllRoleStaffs(options?: RoleFetchOptions): Promise<Array<RoleStaff>> {
    return this.api.getAllRoleStaffs(options)
  }
  async createScopesStaff(source: CreateScopes): Promise<ScopesStaff> {
    const __result = await this.api.createScopesStaff(source);
    this.scopesStaffCache.clear();
    return __result
  }
  async createRoleStaff(source: CreateRole): Promise<RoleStaff> {
    const __result = await this.api.createRoleStaff(source);
    this.roleStaffCache.clear();
    return __result
  }
  async createRolePermissionStaff(source: CreateRolePermission): Promise<RolePermissionStaff> {
    return this.api.createRolePermissionStaff(source)
  }
  async createRoleAssignmentStaff(source: CreateRoleAssignment): Promise<RoleAssignmentStaff> {
    return this.api.createRoleAssignmentStaff(source)
  }
  async createUserPermissionStaff(source: CreateUserPermission): Promise<UserPermissionStaff> {
    return this.api.createUserPermissionStaff(source)
  }
  async updateScopesStaff(source: UpdateScopes): Promise<ScopesStaff> {
    const __result = await this.api.updateScopesStaff(source);
    this.scopesStaffCache.clear();
    return __result
  }
  async updateRoleStaff(source: UpdateRole): Promise<RoleStaff> {
    const __result = await this.api.updateRoleStaff(source);
    this.roleStaffCache.clear();
    return __result
  }
  async updateRolePermissionStaff(source: UpdateRolePermission): Promise<RolePermissionStaff> {
    return this.api.updateRolePermissionStaff(source)
  }
  async updateRoleAssignmentStaff(source: UpdateRoleAssignment): Promise<RoleAssignmentStaff> {
    return this.api.updateRoleAssignmentStaff(source)
  }
  async updateUserPermissionStaff(source: UpdateUserPermission): Promise<UserPermissionStaff> {
    return this.api.updateUserPermissionStaff(source)
  }
  async deleteScopesStaff(source: DeleteScopesStaff): Promise<void> {
    const __result = await this.api.deleteScopesStaff(source);
    this.scopesStaffCache.clear();
    return __result
  }
  async deleteRoleStaff(source: DeleteRoleStaff): Promise<void> {
    const __result = await this.api.deleteRoleStaff(source);
    this.roleStaffCache.clear();
    return __result
  }
  async deleteRolePermissionStaff(source: DeleteRolePermissionStaff): Promise<void> {
    return this.api.deleteRolePermissionStaff(source)
  }
  async deleteRoleAssignmentStaff(source: DeleteRoleAssignmentStaff): Promise<void> {
    return this.api.deleteRoleAssignmentStaff(source)
  }
  async deleteUserPermissionStaff(source: DeleteUserPermissionStaff): Promise<void> {
    return this.api.deleteUserPermissionStaff(source)
  }
  async getScopesStaffRolePermissions(id: string, options?: RolePermissionFetchOptions): Promise<Array<RolePermissionStaff>> {
    return this.api.getScopesStaffRolePermissions(id, options)
  }
  async getScopesStaffUserPermissions(id: string, options?: UserPermissionFetchOptions): Promise<Array<UserPermissionStaff>> {
    return this.api.getScopesStaffUserPermissions(id, options)
  }
  async getRoleStaffPermissions(id: number, options?: RolePermissionFetchOptions): Promise<Array<RolePermissionStaff>> {
    return this.api.getRoleStaffPermissions(id, options)
  }
  async getRoleStaffAssignments(id: number, options?: RoleAssignmentFetchOptions): Promise<Array<RoleAssignmentStaff>> {
    return this.api.getRoleStaffAssignments(id, options)
  }
  async getRolePermissionStaffRoleId(id: number, options?: RoleFetchOptions): Promise<RoleStaff> {
    return this.api.getRolePermissionStaffRoleId(id, options)
  }
  async getRolePermissionStaffScopeId(id: number, options?: ScopesFetchOptions): Promise<ScopesStaff> {
    return this.api.getRolePermissionStaffScopeId(id, options)
  }
  async getRoleAssignmentStaffUserId(id: number, options?: UserStaffFetchOptions): Promise<UserStaff> {
    return this.api.getRoleAssignmentStaffUserId(id, options)
  }
  async getRoleAssignmentStaffRoleId(id: number, options?: RoleFetchOptions): Promise<RoleStaff> {
    return this.api.getRoleAssignmentStaffRoleId(id, options)
  }
  async getRoleAssignmentStaffAssignedBy(id: number, options?: UserStaffFetchOptions): Promise<UserStaff> {
    return this.api.getRoleAssignmentStaffAssignedBy(id, options)
  }
  async getUserPermissionStaffUserId(id: number, options?: UserStaffFetchOptions): Promise<UserStaff> {
    return this.api.getUserPermissionStaffUserId(id, options)
  }
  async getUserPermissionStaffScopeId(id: number, options?: ScopesFetchOptions): Promise<ScopesStaff> {
    return this.api.getUserPermissionStaffScopeId(id, options)
  }
  async getUserPermissionStaffAssignedBy(id: number, options?: UserStaffFetchOptions): Promise<UserStaff> {
    return this.api.getUserPermissionStaffAssignedBy(id, options)
  }
}
